import { Link, useLocation } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@/frontend/components/ui/sidebar";
import { usePatientSummaryQuery } from "@/frontend/queries/dashboard/patients";
import { useWorkspacePersona } from "./individual/useWorkspacePersona";
import {
  INDIVIDUAL_PATIENT_SECTIONS,
  PATIENT_SECTIONS,
} from "@/frontend/types/dashboard/nav";
import { activeSectionFrom } from "@/frontend/utils/dashboard/nav";

/**
 * Sidebar contents while inside one patient, a back link to the table followed
 * by its sections. It replaces the workspace nav rather than nesting beside it.
 * An individual workspace has only the owner, so the back link is dropped and
 * the second section is named for connecting their own systems.
 */
export function PatientRailNav({ patientId }: { patientId: string }) {
  const location = useLocation();
  const active = activeSectionFrom(location.pathname);
  const { data: summary } = usePatientSummaryQuery(patientId);
  const { isIndividual } = useWorkspacePersona();
  const sections = isIndividual ? INDIVIDUAL_PATIENT_SECTIONS : PATIENT_SECTIONS;

  return (
    <>
      {!isIndividual && (
        <>
          <SidebarGroup className="pb-0">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="All patients" className="text-muted-foreground">
                    <Link to="/dashboard/patients" search={{}}>
                      <ArrowLeft />
                      <span>All patients</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarSeparator className="mx-0 w-full" />
        </>
      )}

      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {sections.map((section) => {
              const showDot = section.id === "onboarding" && (summary?.openIssueCount ?? 0) > 0;
              return (
                <SidebarMenuItem key={section.id}>
                  <SidebarMenuButton
                    asChild
                    isActive={active === section.id}
                    tooltip={section.label}
                  >
                    <Link
                      to={`/dashboard/patients/$patientId/${section.id}`}
                      params={{ patientId }}
                    >
                      <section.icon />
                      <span>{section.label}</span>
                    </Link>
                  </SidebarMenuButton>
                  {showDot && (
                    <SidebarMenuBadge>
                      <span className="size-1.5 rounded-full bg-amber-500" aria-label="Needs attention" />
                    </SidebarMenuBadge>
                  )}
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
}
